import { useState, useEffect, useCallback, useRef, useMemo } from "react"; // Keep combined import
import { supabase as supabaseClient } from "@/integrations/supabase/client";
import { toast } from "@/hooks/ui/use-toast"; // Corrected import path for toast
import { Review, Reward, Business, Stat, CustomerData, AnalyticsData, StatTrend, ReviewSentiment, BusinessDetails, TimeDataPoint, SentimentDataPoint, SourceDataPoint } from "@/types/index"; // Added BusinessDetails and analytics types
import { QrCodeData } from "@/types/qrCode";
import { Database } from "@/integrations/supabase/types"; // Import Supabase database types




// Define placeholder data within the hook or import if shared
// Define a placeholder for the detailed business data
const placeholderBusinessDetails: BusinessDetails = {
  id: "",
  name: "Loading...",
  description: "",
  address: "",
  website: "",
  phone: "",
  created_at: "",
  user_id: "",
  // Add other fields from your BusinessDetails type if they exist
};

const placeholderTrend: StatTrend = { value: 0, isPositive: true };
const placeholderStats: Stat[] = [
  { title: "Total Reviews", value: "0", description: "N/A", icon: "MessageSquare", trend: placeholderTrend },
  { title: "Average Rating", value: "0.0", description: "N/A", icon: "Star", trend: placeholderTrend },
  { title: "Customers Engaged", value: "0", description: "N/A", icon: "Users", trend: placeholderTrend }
];
const placeholderRewards: Reward[] = [];
const placeholderCustomerData: CustomerData = {
  name: "Customer", points: 0, membership: "Standard", expiryDate: "N/A", shopName: "Your Shop", membershipId: "N/A", membershipLevel: "Standard"
};
const placeholderAnalyticsData: AnalyticsData = {
  reviewsOverTime: [], sentimentAnalysis: [], reviewSources: [],
};

// Define the type for the reviews fetched from Supabase
type SupabaseReview = Database['public']['Tables']['reviews']['Row'];

export function useBusinessData(selectedBusinessId: string | null, businesses: Business[], refreshTrigger: number) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [shopStats, setShopStats] = useState<Stat[]>(placeholderStats);
  const [rewards, setRewards] = useState<Reward[]>(placeholderRewards);
  const [customerData, setCustomerData] = useState<CustomerData>(placeholderCustomerData);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>(placeholderAnalyticsData);
  const [selectedBusinessDetails, setSelectedBusinessDetails] = useState<BusinessDetails | null>(null); // State for full business details
  const [loadingData, setLoadingData] = useState(false); // Loading state for DB data

  // Ref to track the previous selectedBusinessId to detect transitions to null
  const prevSelectedBusinessIdRef = useRef<string | null | undefined>(undefined); // Use undefined for initial state


  // --- Function to fetch QR Codes ---
  const fetchQrCodesForBusiness = useCallback(async (businessId: string) => {
    try {
      const { data, error } = await supabaseClient
        .from("qr_codes")
        .select("*")
        .eq("business_id", businessId);
      if (error) throw error;
      const validQrCodes = (data || []).filter(
        (qr): qr is Database['public']['Tables']['qr_codes']['Row'] & { type: 'review' | 'loyalty' | 'job-posting' } =>
          qr.type === 'review' || qr.type === 'loyalty' || qr.type === 'job-posting'
      ) as QrCodeData[];
      return validQrCodes;
    } catch (error) {
      toast.error("Failed to load QR codes.", { description: (error as Error).message || "An unexpected error occurred." });
      return [];
    }
  }, []);

  // --- Derive selectedBusinessDetails from businesses array (single source of truth) ---
  // This uses the data already fetched by BusinessContext instead of re-fetching
  useEffect(() => {
    if (!selectedBusinessId) {
      setSelectedBusinessDetails(null);
      return;
    }

    // Find business from the already-loaded businesses array (from BusinessContext)
    const business = businesses?.find(b => b.id === selectedBusinessId);
    if (business) {
      // Transform Business to BusinessDetails format
      const details: BusinessDetails = {
        id: business.id,
        name: business.name || "Unnamed Business",
        description: (business as any).description || "",
        address: business.address || "",
        website: (business as any).website || "",
        phone: (business as any).phone || "",
        google_place_id: (business as any).google_place_id || null,
        created_at: business.created_at || new Date().toISOString(),
        user_id: (business as any).user_id || "",
        country_code: business.country_code || null,
        allow_shift_self_assignment: (business as any).allow_shift_self_assignment || false,
        timezone: business.timezone || null,
        // Use opening_hours from BusinessContext (single source of truth)
        opening_hours: business.opening_hours || null,
      };
      setSelectedBusinessDetails(details);
    } else {
      setSelectedBusinessDetails(null);
    }
  }, [selectedBusinessId, businesses]);

  // --- Effect to fetch core business data from DB (reviews only) ---
  useEffect(() => {

    // Check if selectedBusinessId has changed from a non-null value to null
    const hasSelectedBusinessIdChangedToNull = prevSelectedBusinessIdRef.current !== undefined && prevSelectedBusinessIdRef.current !== null && selectedBusinessId === null;

    if (!selectedBusinessId) {
      // Only reset if selectedBusinessId just became null, or if it's initially null and states are not already reset
      if (hasSelectedBusinessIdChangedToNull || (reviews.length > 0 || rewards.length > 0)) {
        setReviews([]); // Clear all reviews
        setShopStats(placeholderStats);
        setRewards(placeholderRewards);
        setCustomerData(placeholderCustomerData);
        setAnalyticsData(placeholderAnalyticsData);
        setLoadingData(false);
      }
      prevSelectedBusinessIdRef.current = selectedBusinessId; // Update ref
      return;
    }

    // If selectedBusinessId is not null, ensure the ref is updated and proceed with data fetching
    prevSelectedBusinessIdRef.current = selectedBusinessId;

    // Find the current business name for placeholder updates (non-blocking)
    const currentBusinessName = businesses?.find(b => b.id === selectedBusinessId)?.name || "Loading...";
    // Update shop name immediately for better UX
    setCustomerData(prev => ({ ...placeholderCustomerData, shopName: currentBusinessName }));

    const fetchCoreData = async (businessId: string) => {
      setLoadingData(true);
      // Only clear reviews if they are not already empty, to avoid unnecessary re-renders
      if (reviews.length > 0) {
        setReviews([]); // Clear previous reviews for the new business ID
      }
      let internalReviews: Review[] = []; // Temporary store for internal reviews

      try {
        // Fetch reviews only - business details come from BusinessContext
        const reviewsResponse = await supabaseClient
          .from("reviews")
          .select(`
            id,
            business_id,
            customer_name,
            customer_email,
            created_at,
            rating,
            content,
            sentiment,
            source,
            published,
            form_id,
            reply,
            reward_email_sent_at,
            reply_email_sent_at,
            session_id,
            review_forms (
              id,
              title
            )
          `)
          .eq("business_id", businessId);

        // Process Internal Reviews
        if (reviewsResponse.error) throw reviewsResponse.error;
        const fetchedDbReviews = reviewsResponse.data || [];
        const formattedDbReviews = fetchedDbReviews.map((review: any): Review => ({
          id: review.id,
          business_id: review.business_id,
          customerName: review.customer_name || "",
          customerEmail: review.customer_email || null,
          date: review.created_at || new Date().toISOString(),
          rating: review.rating,
          text: review.content || "",
          sentiment: (review.sentiment as ReviewSentiment) || "neutral",
          source: review.source || "Internal",
          published: review.published !== false,
          created_at: review.created_at || new Date().toISOString(),
          tag_id: review.form_id,
          formTitle: review.review_forms?.title || null,
          reply: review.reply || null,
          reward_email_sent_at: review.reward_email_sent_at || null,
          reply_email_sent_at: review.reply_email_sent_at || null,
          session_id: review.session_id || null,
        }));
        internalReviews = formattedDbReviews;
        // Set internal reviews to state immediately
        setReviews(internalReviews);


        // --- Calculate Stats & Analytics based on internal reviews ---
        const totalReviews = internalReviews.length;
        const averageRating = totalReviews > 0
          ? (internalReviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews).toFixed(1)
          : "0.0";
        const uniqueCustomers = new Set(internalReviews.map(r => r.customerName)).size;
        const newReviewsCount = internalReviews.filter(review => !review.published).length;

        // Calculate Reviews Over Time Data
        const reviewsByMonth: { [key: string]: { positive: number; negative: number; neutral: number } } = {};
        internalReviews.forEach(review => {
          if (review.created_at) {
            const date = new Date(review.created_at);
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const monthYear = `${year}-${month}`;

            if (!reviewsByMonth[monthYear]) {
              reviewsByMonth[monthYear] = { positive: 0, negative: 0, neutral: 0 };
            }

            let reviewSentiment: ReviewSentiment = 'neutral';
             if (review.rating !== undefined && review.rating !== null) {
               if (review.rating >= 4) {
                 reviewSentiment = 'positive';
               } else if (review.rating <= 2) {
                 reviewSentiment = 'negative';
               }
             } else if (review.sentiment) {
                reviewSentiment = review.sentiment;
             }

            reviewsByMonth[monthYear][reviewSentiment] = (reviewsByMonth[monthYear][reviewSentiment] || 0) + 1;
          }
        });

        const timeData: TimeDataPoint[] = Object.keys(reviewsByMonth)
          .map(date => ({
            date: date,
            positive: reviewsByMonth[date].positive || 0,
            negative: reviewsByMonth[date].negative || 0,
            neutral: reviewsByMonth[date].neutral || 0,
          }))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        // Calculate Trend (Simple: Compare current month to previous month)
        let trendValue = 0;
        let isPositiveTrend = true;
        if (timeData.length >= 2) {
            const currentMonthData = timeData[timeData.length - 1];
            const previousMonthData = timeData[timeData.length - 2];
            const currentMonthTotal = currentMonthData.positive + currentMonthData.negative + currentMonthData.neutral;
            const previousMonthTotal = previousMonthData.positive + previousMonthData.negative + previousMonthData.neutral;
            trendValue = currentMonthTotal - previousMonthTotal;
            isPositiveTrend = trendValue >= 0;
        } else if (timeData.length === 1) {
             const currentMonthData = timeData[0];
             trendValue = currentMonthData.positive + currentMonthData.negative + currentMonthData.neutral;
             isPositiveTrend = trendValue >= 0;
        }

        const calculatedStats: Stat[] = [
          { title: "Total Reviews", value: totalReviews.toString(), description: "Internal reviews", icon: "MessageSquare", trend: placeholderTrend },
          { title: "Average Rating", value: averageRating, description: `Based on ${totalReviews} reviews`, icon: "Star", trend: placeholderTrend },
          { title: "New Reviews", value: newReviewsCount.toString(), description: "Not yet published", icon: "MessageSquare", trend: { value: newReviewsCount, isPositive: newReviewsCount >= 0 } },
          { title: "Review Trend", value: trendValue.toString(), description: "Vs. previous month", icon: "MessageSquare", trend: { value: trendValue, isPositive: isPositiveTrend } },
          { title: "Customers Engaged", value: uniqueCustomers.toString(), description: "Unique reviewers", icon: "Users", trend: placeholderTrend },
          { title: "Rewards Redeemed", value: "0", description: "Feature coming soon", icon: "Gift", trend: placeholderTrend }
        ];
        // Update stats
        setShopStats(calculatedStats);

        // Fetch Rewards (Placeholder)
        setRewards([]);

        // Prepare Analytics Data (using internal reviews only)
        const sentimentCounts = internalReviews.reduce((acc, review) => {
            let reviewSentiment: ReviewSentiment = 'neutral';
             if (review.rating !== undefined && review.rating !== null) {
               if (review.rating >= 4) {
                 reviewSentiment = 'positive';
               } else if (review.rating <= 2) {
                 reviewSentiment = 'negative';
               }
             } else if (review.sentiment) {
                reviewSentiment = review.sentiment;
             }
            acc[reviewSentiment] = (acc[reviewSentiment] || 0) + 1;
            return acc;
          }, {} as Record<ReviewSentiment, number>);

        // Calculate Review Sources Data (internal only)
        const sourceCounts: { [key: string]: number } = {};
        internalReviews.forEach(review => {
            const sourceName = review.tagName || "Internal";
            sourceCounts[sourceName] = (sourceCounts[sourceName] || 0) + 1;
        });

        const reviewSourcesData: SourceDataPoint[] = Object.keys(sourceCounts).map(sourceName => {
            const color = "#8b5cf6"; // Purple for internal sources
            return { name: sourceName, value: sourceCounts[sourceName], color: color };
        }).filter(source => source.value > 0);

        const newAnalyticsData = {
          reviewsOverTime: timeData,
          sentimentAnalysis: [
            { name: "Positive", value: sentimentCounts.positive || 0, color: "#22c55e" },
            { name: "Neutral", value: sentimentCounts.neutral || 0, color: "#94a3b8" },
            { name: "Negative", value: sentimentCounts.negative || 0, color: "#ef4444" }
          ],
          reviewSources: reviewSourcesData,
        };
        // Update analytics data
        setAnalyticsData(newAnalyticsData as AnalyticsData);


      } catch (error: unknown) {
        // Reset all states to placeholders on error
        setReviews([]);
        setShopStats(placeholderStats);
        setRewards(placeholderRewards);
        setCustomerData(prev => ({ ...placeholderCustomerData, shopName: prev.shopName }));
        setAnalyticsData(placeholderAnalyticsData);
        setSelectedBusinessDetails(null);
      } finally {
        setLoadingData(false);
      }
    };

    fetchCoreData(selectedBusinessId);

  }, [selectedBusinessId, refreshTrigger]);

  // Separate effect to update business name when businesses array loads (non-blocking)
  useEffect(() => {
    if (selectedBusinessId && businesses && businesses.length > 0) {
      const currentBusinessName = businesses.find(b => b.id === selectedBusinessId)?.name;
      if (currentBusinessName) {
        setCustomerData(prev => ({ ...prev, shopName: currentBusinessName }));
      }
    }
  }, [selectedBusinessId, businesses]);

  return {
    reviews, // Internal reviews only
    shopStats, // Calculated based on internal reviews
    rewards,
    customerData,
    analyticsData, // Calculated based on internal reviews
    selectedBusinessDetails,
    setSelectedBusinessDetails,
    loadingData, // Loading state for UI
    setLoadingData,
    fetchQrCodesForBusiness,
  };
}
